import { useEffect, useState } from "react";
import { useApiClient } from "../../../apiClient";
import {
    FRAME_POLL_INTERVAL_MS,
    FRAME_POLL_TIMEOUT_MS,
    FrameAgeAnchor,
    parseFrameAgeHeader,
} from "../utils/camera-aim.utils";
import { isPageVisible, withTimeout } from "./useCameraLiveViewSession";

export interface CameraLiveViewFrame {
    /** Object URL of the picture on screen; `null` until the session's first frame. */
    imageUrl: string | null;
    /** How old that picture was when last confirmed, and when — see `frameAgeMs`. `null` = unknown. */
    anchor: FrameAgeAnchor | null;
    /**
     * The relay answered for another session (`X-Live-View-Session`), or went
     * empty after showing a frame: the session may have ended or changed hands.
     * The page asks the session hook to check at once instead of waiting up to
     * 15 s for the next renewal — and a foreign session's picture is never shown.
     */
    sessionSuspect: boolean;
}

const EMPTY: CameraLiveViewFrame = { imageUrl: null, anchor: null, sessionSuspect: false };

/**
 * The latest aim-view frame of `sessionId`, polled every second
 * (docs/epics/camera-aim-live-view.md).
 *
 * The relay answers raw `image/jpeg` behind the dashboard's auth header, so it
 * cannot be an `<img src>`: it is fetched, turned into an object URL, and the
 * previous URL is revoked when replaced and on unmount.
 *
 * - `200` — a new picture; its `ETag` is sent back as `If-None-Match`.
 * - `304` — same picture: keep it, re-anchor its age (`X-Frame-Age-Ms`).
 * - `204` — nothing to show (box not streaming yet, or no session any more).
 *
 * Never two polls in flight: a tick that finds one running is skipped, so a
 * slow 4G link degrades to "as fast as it answers", not a pile-up. A poll
 * unanswered after `FRAME_POLL_TIMEOUT_MS` is aborted and its late answer
 * ignored, so one hung request cannot freeze the picture for good. Paused
 * while the page is hidden, like the lease. A failed poll keeps the picture —
 * its age keeps growing, which is the honest signal on a ladder.
 *
 * The relay serves only a REGISTERED viewer — one that started or renewed
 * the current session within its lease (`useCameraLiveViewSession` does) —
 * and answers 204 to anyone else.
 *
 * Not React Query: the body is a Blob that must be revoked, never cached.
 */
export function useCameraLiveViewFrame({
    agentId,
    organizationId,
    sessionId,
    enabled = true,
}: {
    agentId: string;
    organizationId: string;
    /** The session whose frames to show; `null` = no session, nothing polled. */
    sessionId: string | null;
    enabled?: boolean;
}): CameraLiveViewFrame {
    const client = useApiClient();
    const [frame, setFrame] = useState<CameraLiveViewFrame>(EMPTY);

    useEffect(() => {
        if (!enabled || !agentId || !organizationId || !sessionId) return undefined;
        let disposed = false;
        let inFlight = false;
        /** The poll on the wire: aborted on unmount, or when it times out. */
        let current: { controller: AbortController | null; stale: boolean } | null = null;
        let objectUrl: string | null = null;
        let etag: string | null = null;

        const read = async (attempt: { controller: AbortController | null; stale: boolean }): Promise<void> => {
            const response = await client.fetchRaw({
                endpoint: `court-recordings/admin/venue-agents/${encodeURIComponent(agentId)}/live-view/frame?organizationId=${encodeURIComponent(organizationId)}`,
                method: 'GET',
                headers: etag ? { 'If-None-Match': etag } : undefined,
                signal: attempt.controller?.signal,
            });
            if (disposed || attempt.stale) return;
            const receivedAt = Date.now();

            const frameSession = response.headers.get('X-Live-View-Session');
            if (frameSession && frameSession !== sessionId) {
                setFrame((prev) => (prev.sessionSuspect ? prev : { ...prev, sessionSuspect: true }));
                return;
            }
            if (response.status === 204) {
                if (objectUrl) {
                    setFrame((prev) => (prev.sessionSuspect ? prev : { ...prev, sessionSuspect: true }));
                }
                return;
            }

            const ageMs = parseFrameAgeHeader(response.headers.get('X-Frame-Age-Ms'));
            const anchor = ageMs === null ? null : { ageMs, receivedAt };
            if (response.status === 304) {
                if (objectUrl) {
                    setFrame((prev) => ({ ...prev, anchor: anchor ?? prev.anchor, sessionSuspect: false }));
                }
                return;
            }

            const blob = await response.blob();
            if (disposed || attempt.stale) return;
            const nextUrl = URL.createObjectURL(blob);
            const previousUrl = objectUrl;
            objectUrl = nextUrl;
            etag = response.headers.get('ETag');
            setFrame({ imageUrl: nextUrl, anchor, sessionSuspect: false });
            // The <img> re-renders onto the new URL; the decoded picture
            // on screen does not need the old one.
            if (previousUrl) URL.revokeObjectURL(previousUrl);
        };

        const poll = async (): Promise<void> => {
            if (disposed || inFlight || !isPageVisible()) return;
            inFlight = true;
            const attempt = {
                controller: typeof AbortController === 'undefined' ? null : new AbortController(),
                stale: false,
            };
            current = attempt;
            try {
                await withTimeout(read(attempt), FRAME_POLL_TIMEOUT_MS, () => {
                    attempt.stale = true;
                    attempt.controller?.abort();
                });
            } catch {
                // Keep the picture; its age keeps growing (see above).
            } finally {
                inFlight = false;
                if (current === attempt) current = null;
            }
        };

        void poll();
        const interval = setInterval(() => void poll(), FRAME_POLL_INTERVAL_MS);
        const handleVisibility = (): void => {
            if (isPageVisible()) void poll();
        };
        document.addEventListener('visibilitychange', handleVisibility);

        return () => {
            disposed = true;
            clearInterval(interval);
            document.removeEventListener('visibilitychange', handleVisibility);
            current?.controller?.abort();
            if (objectUrl) URL.revokeObjectURL(objectUrl);
            objectUrl = null;
            setFrame(EMPTY);
        };
    }, [client, enabled, agentId, organizationId, sessionId]);

    return frame;
}
